export const POSTS = [
  {
    slug: "survey-drafting-in-texas-counties",
    title: "Survey Drafting in Texas: What 254 Counties Teach You About County Formats",
    description:
      "Texas is a metes-and-bounds state with 254 counties and just as many recording nuances — here's how drafting production stays consistent across them.",
    date: "2026-10-22",
    readMinutes: 6,
    tag: "County Spotlight",
    relatedService: "subdivision-plat-drafting",
    sections: [
      {
        h: "One state, 254 recording offices",
        paras: [
          "Texas has more counties than any other state, and every one of them records plats a little differently — sheet sizes, margins, certification blocks, and filing checklists vary from Harris County to the smallest rural office. For a surveying firm working across counties, that variation is where production time quietly disappears.",
          "The fix is procedural: keep a per-county format sheet, and make sure whoever drafts the plat follows it exactly.",
        ],
      },
      {
        h: "Metes and bounds, all the way down",
        paras: [
          "Unlike PLSS states, Texas describes land by courses, distances, and monument calls against original surveys and leagues. Drafting a Texas boundary or subdivision plat means respecting those calls precisely — the description on the sheet must match the record, course for course.",
        ],
      },
      {
        h: "What we draft for Texas firms",
        list: [
          "Subdivision and record plats to the county format you direct",
          "Boundary and metes-and-bounds descriptions on the sheet",
          "ALTA/NSPS title surveys for Texas commercial transactions",
          "Topographic and construction staking plans statewide",
        ],
      },
      {
        h: "County pages",
        paras: ["See our localized coverage for the counties your firm works in most:"],
        links: [
          { label: "Harris County", to: "/service-areas/texas/county/harris-county" },
          { label: "Dallas County", to: "/service-areas/texas/county/dallas-county" },
          { label: "Tarrant County", to: "/service-areas/texas/county/tarrant-county" },
          { label: "Travis County", to: "/service-areas/texas/county/travis-county" },
          { label: "Bexar County", to: "/service-areas/texas/county/bexar-county" },
          { label: "All 254 Texas counties", to: "/service-areas/texas" },
        ],
      },
      {
        h: "The takeaway",
        paras: [
          "County format fluency is a production habit, not a talent. With your format sheets on file, drafting across Texas counties becomes routine — that's the model behind our subdivision plat drafting service.",
        ],
      },
    ],
  },
  {
    slug: "survey-drafting-in-california-counties",
    title: "Survey Drafting in California: Records, Plats and 58 Counties",
    description:
      "California's 58 counties each carry their own recording expectations — a practical look at keeping survey drafting consistent across the state.",
    date: "2026-10-22",
    readMinutes: 5,
    tag: "County Spotlight",
    relatedService: "land-survey-cad-drafting",
    sections: [
      {
        h: "A PLSS state with layers of history",
        paras: [
          "California surveys run on the Public Land Survey System, but ranchos and older grants still shape boundaries in many areas. Drafting here means section breakdowns and township-range ties alongside rancho calls — exactly as the record and your field work establish them.",
        ],
      },
      {
        h: "County and city expectations",
        paras: [
          "Parcel maps, tract maps, records of survey — each filing type has its own sheet expectations, and counties from Los Angeles to Shasta enforce their own checklists. Production stays smooth when the drafting follows a per-county format sheet rather than a generic template.",
        ],
      },
      {
        h: "County pages",
        paras: ["Explore localized coverage across California:"],
        links: [
          { label: "Los Angeles County", to: "/service-areas/california/county/los-angeles-county" },
          { label: "San Diego County", to: "/service-areas/california/county/san-diego-county" },
          { label: "Orange County", to: "/service-areas/california/county/orange-county" },
          { label: "Riverside County", to: "/service-areas/california/county/riverside-county" },
          { label: "All 58 California counties", to: "/service-areas/california" },
        ],
      },
      {
        h: "The takeaway",
        paras: [
          "Whether it's a record of survey in a coastal county or a tract map inland, our land survey CAD drafting service produces the sheets while your licensed California surveyor keeps review and certification.",
        ],
      },
    ],
  },
  {
    slug: "survey-drafting-in-florida-counties",
    title: "Survey Drafting in Florida: Plats, Elevation Certificates and 67 Counties",
    description:
      "Florida drafting runs on plats and flood documentation — how surveying firms keep production consistent across all 67 counties.",
    date: "2026-10-22",
    readMinutes: 5,
    tag: "County Spotlight",
    relatedService: "fema-elevation-certificate-drafting",
    sections: [
      {
        h: "A plat-driven state",
        paras: [
          "Florida is a PLSS state with a strong plat culture — subdivision plats dominate the recording workflow, and each county's checklist is enforced at review. Consistent sheet production across counties comes from per-county format discipline, not improvisation.",
        ],
      },
      {
        h: "Flood documentation is everyday work",
        paras: [
          "Few states see as much elevation certificate volume as Florida. Drafting support for FEMA elevation certificates — diagrams, building sketches, and section documentation from your field measurements — is one of the most-requested production tasks from Florida firms.",
        ],
      },
      {
        h: "County pages",
        paras: ["See our localized coverage across Florida:"],
        links: [
          { label: "Miami-Dade County", to: "/service-areas/florida/county/miami-dade-county" },
          { label: "Broward County", to: "/service-areas/florida/county/broward-county" },
          { label: "Palm Beach County", to: "/service-areas/florida/county/palm-beach-county" },
          { label: "Orange County", to: "/service-areas/florida/county/orange-county" },
          { label: "All 67 Florida counties", to: "/service-areas/florida" },
        ],
      },
      {
        h: "The takeaway",
        paras: [
          "Plats and elevation certificates reward the same thing: accurate, format-correct sheets delivered on schedule. Our FEMA elevation certificate drafting and plat production services are built for exactly that volume.",
        ],
      },
    ],
  },
  {
    slug: "dtm-vs-dem-difference",
    title: "DTM vs DEM: What's the Difference and When Each Matters",
    description:
      "Digital terrain models and digital elevation models sound interchangeable but serve different drafting purposes — here's how survey offices actually use each.",
    date: "2026-10-17",
    readMinutes: 5,
    tag: "Terrain",
    relatedService: "grading-earthwork-drafting",
    sections: [
      {
        h: "The short version",
        paras: [
          "A DEM (digital elevation model) is a grid of elevation values — typically bare-earth, sampled at regular intervals. A DTM (digital terrain model) adds the features that shape the ground: breaklines along ridges, toes, and edges of pavement, plus spot elevations at key points. The DTM is what makes contours behave correctly at hard edges.",
        ],
      },
      {
        h: "Why breaklines change everything",
        paras: [
          "Contouring a DEM grid alone produces contours that bleed across kerbs, ditches, and retaining walls. Breaklines tell the surface where slopes change abruptly. In practice, a survey-grade DTM is a DEM grid constrained by field-coded breaklines — and that difference is exactly what reviewers look for on a grading plan.",
        ],
      },
      {
        h: "When a DEM is enough",
        paras: [
          "For regional analysis, watershed studies, and visualization, a published DEM grid is usually sufficient. It's fast to obtain and easy to theme. The moment design decisions depend on the surface — drainage paths near structures, cut-fill balance, road tie-ins — you need the DTM.",
        ],
      },
      {
        h: "What drafters need to build either one",
        list: [
          "Point files with codes that identify breakline features",
          "Breakline strings or clear field coding instructions",
          "The target grid interval and coordinate system",
          "Your surface and contour display styles",
        ],
        paras: [
          "With those in place, existing-ground surfaces and contour drawings can be produced directly from your field data — see how our grading and earthwork drafting service handles surface documentation.",
        ],
      },
    ],
  },
  {
    slug: "gis-and-cad-in-land-surveying",
    title: "How GIS and CAD Work Together in Land Surveying",
    description:
      "CAD draws the survey; GIS connects it to everything else. A practical look at how surveying offices move data between the two worlds without losing structure.",
    date: "2026-10-17",
    readMinutes: 6,
    tag: "GIS",
    relatedService: "gis-data-conversion",
    sections: [
      {
        h: "Two tools, two jobs",
        paras: [
          "CAD is built for precise drawing: exact coordinates, linework, annotation, and sheets. GIS is built for analysis and management: attributes, spatial queries, and datasets that join parcels to ownership, zoning, and infrastructure. A survey office needs both — the drawing for the deliverable, the dataset for everything the data will feed later.",
        ],
      },
      {
        h: "The usual path: CAD to GIS",
        paras: [
          "Survey data typically starts life in CAD. Converting it to GIS means more than exporting geometry: features need the right types, closed parcels must become polygons, and attributes — lot numbers, areas, record references — must land in the right fields. That structured conversion is what makes a drawing usable as data.",
        ],
      },
      {
        h: "The return path: GIS to CAD",
        paras: [
          "The reverse happens just as often: pulling existing parcel fabrics, utility networks, or environmental layers into a CAD base plan as reference. Here the priorities are coordinate reference accuracy and sensible layer organization, so reference data stays clearly separated from survey linework.",
        ],
      },
      {
        h: "Where conversions go wrong",
        list: [
          "Coordinate reference mismatches — data landing metres or feet away from truth",
          "Lost attribution — shapes survive but their meaning doesn't",
          "Geometry type errors — open parcel lines that should be closed polygons",
          "Unsorted layers — everything arrives in one undifferentiated blob",
        ],
      },
      {
        h: "Making it routine",
        paras: [
          "Offices that move data both directions regularly benefit from a fixed schema: agreed layer-to-feature mappings, named attribute fields, and a documented CRS. Once that exists, conversion becomes a repeatable production task — exactly the kind our GIS data conversion service handles against your specification.",
        ],
      },
    ],
  },
  {
    slug: "survey-cad-drawing-quality-control",
    title: "Survey CAD Drawing Quality Control: Best Practices",
    description:
      "A practical QC checklist for survey CAD production — the checks that catch errors before a drawing reaches your reviewer or your client.",
    date: "2026-10-17",
    readMinutes: 6,
    tag: "Quality",
    relatedService: "land-survey-cad-drafting",
    sections: [
      {
        h: "Why QC is a process, not a glance",
        paras: [
          "Most drawing errors are boring: a missed layer, a stale note, a mislabeled distance. They survive because review happens under time pressure. A written checklist turns QC from a talent into a procedure — anyone on the team can run it, and every drawing gets the same scrutiny.",
        ],
      },
      {
        h: "Geometry checks",
        list: [
          "Boundary closure verified against stated bearings and distances",
          "Linework on correct layers with correct linetypes and weights",
          "No duplicate or zero-length objects",
          "Coordinates in the agreed system and units",
        ],
      },
      {
        h: "Annotation checks",
        list: [
          "Every boundary line labelled with bearing and distance",
          "Monument calls match the field records",
          "Lot, block, subdivision, and area notes present and consistent",
          "Title block fields complete — project, sheet, revision, date",
        ],
      },
      {
        h: "Presentation checks",
        paras: [
          "Plot the sheet to PDF before delivery and review the plot, not the model space. Overlaps, unreadable text at scale, and clipped annotation only show up on paper. Compare against the project's example sheet for style drift.",
        ],
      },
      {
        h: "Source-fidelity checks",
        paras: [
          "Every figure on the drawing traces to supplied data — a point file, a record, a written instruction. Anything that had to be interpreted gets flagged for the surveyor rather than silently resolved. This discipline is what keeps drafting support safe to certify over.",
        ],
      },
      {
        h: "Making QC stick",
        paras: [
          "Attach the checklist to the project folder and require it with every handover. When drafting is outsourced, the same checklist defines acceptance — our land survey CAD drafting production runs against the client's checklist plus our own internal one, so drawings arrive already double-checked.",
        ],
      },
    ],
  },
  {
    slug: "how-to-prepare-topographic-survey-drawings",
    title: "How to Prepare Topographic Survey Drawings",
    description:
      "From point file to finished topo sheet: the production sequence for turning field-coded survey data into a clean, review-ready topographic drawing.",
    date: "2026-10-17",
    readMinutes: 6,
    tag: "Topographic",
    relatedService: "topographic-survey-drafting",
    sections: [
      {
        h: "Start with coded field data",
        paras: [
          "Good topo drawings begin in the field. Point codes that identify feature type and breakline membership let the office build surfaces and linework without detective work. Before drafting, normalize the point file: consistent codes, correct coordinate system, no orphan points.",
        ],
      },
      {
        h: "Build the ground model first",
        paras: [
          "Generate the surface from points and breaklines before drafting any features. Contours derived from the surface will be consistent; hand-drawn contour fixes come later only where the data genuinely requires them, and always documented.",
        ],
      },
      {
        h: "Draft features in passes",
        list: [
          "Pass 1: boundary, control, and reference framework",
          "Pass 2: natural terrain features and drainage",
          "Pass 3: constructed features — pavement, structures, fences",
          "Pass 4: utilities and services as marked or mapped",
          "Pass 5: annotation, spot levels, and sheet assembly",
        ],
        paras: [
          "Passes keep the drawing organized and make review faster — each pass is a self-contained check.",
        ],
      },
      {
        h: "Annotation that reads at plot scale",
        paras: [
          "Spot elevations at grade breaks, feature labels that don't collide with linework, and a legend that matches the symbols actually used. Set text heights for the final plot scale, not for screen comfort — the sheet is the deliverable.",
        ],
      },
      {
        h: "Final checks before issue",
        paras: [
          "Verify contour behaviour at breaklines, confirm every feature class from the scope is present, and plot a check set. When production runs through our topographic survey drafting service, these checks are part of the handover — your reviewer receives a sheet that's already been through a documented pass.",
        ],
      },
    ],
  },
  {
    slug: "how-to-convert-point-cloud-data-to-cad",
    title: "How to Convert Point Cloud Data to CAD Drawings",
    description:
      "A practical workflow for turning LiDAR and laser scan point clouds into clean, layer-organized CAD drawings — from dataset preparation to traced planimetric linework and QA.",
    date: "2026-10-17",
    readMinutes: 7,
    tag: "Point Cloud",
    relatedService: "point-cloud-to-cad-conversion",
    sections: [
      {
        h: "Why point clouds need a drafting step",
        paras: [
          "A point cloud is a measurement, not a drawing. Laser scanners and LiDAR capture millions of coordinated points, but clients, reviewers, and permit authorities expect plans: linework, symbols, dimensions, and annotation on a titled sheet. The conversion step — often called scan-to-CAD — is where the cloud becomes a deliverable.",
          "The work is mostly disciplined tracing: identifying features in the cloud, drawing them as clean CAD geometry, and organizing everything on a layer structure that matches the office standard.",
        ],
      },
      {
        h: "Step 1: Prepare and decimate the dataset",
        paras: [
          "Full-resolution clouds are often far larger than drafting requires. Start by confirming the coordinate system and units, then decimate or tile the dataset to a workable density. Common exchange formats are LAS, LAZ, and E57; many teams export decimated copies per area rather than shipping an entire capture.",
          "Keep the original registration untouched. Drafting should always reference the registered, controlled cloud your surveyor has approved.",
        ],
      },
      {
        h: "Step 2: Define the feature list before tracing",
        paras: [
          "The most common point cloud conversion failure is scope drift: tracing everything, or guessing what matters. Agree a written feature list first — which structures, edges, levels, and fixtures are required, and at what level of detail.",
          "A good feature list names the layer, the symbol or linetype, and the tolerance for each feature class. It turns a judgment call into a checklist.",
        ],
      },
      {
        h: "Step 3: Trace planimetric linework",
        paras: [
          "Plan-view extraction is the core of most conversions: building outlines, pavement edges, fences, drainage features, and visible utilities traced from intensity imagery and slice views. Work in horizontal slices at the heights that best reveal each feature class.",
          "Draft directly on the target layer structure rather than sorting later. It is faster and produces cleaner files.",
        ],
      },
      {
        h: "Step 4: Sections and elevations from scans",
        paras: [
          "Terrestrial scan positions give excellent material for building elevations and cross-sections. Cut the section, trace the profile, and annotate openings, levels, and materials as instructed. Keep section references consistent with the plan sheets so the set reads as one document.",
        ],
      },
      {
        h: "Step 5: QA against the cloud",
        paras: [
          "Every converted drawing should pass a coverage check: drafted features overlaid against the cloud to catch omissions, and dimensions spot-checked against stated references. Anything ambiguous in the data gets flagged for the surveyor's direction — never guessed.",
        ],
      },
      {
        h: "When to outsource point cloud to CAD conversion",
        paras: [
          "Conversion is production-intensive but judgment-light once the feature list is fixed, which makes it ideal for outsourcing. Surveying firms keep capture, registration, and certification in-house and send the tracing to a drafting partner working on their templates.",
          "If your team has clouds waiting for drafting capacity, our point cloud to CAD conversion service is built exactly for this workflow.",
        ],
      },
    ],
  },
  {
    slug: "how-to-outsource-land-survey-cad-drafting",
    title: "How to Outsource Land Survey CAD Drafting Without Losing Quality Control",
    description:
      "A practical guide for surveying firms outsourcing CAD drafting: what to prepare, how to structure instructions, and how to keep review and certification firmly in-house.",
    date: "2026-10-17",
    readMinutes: 6,
    tag: "Outsourcing",
    relatedService: "land-survey-cad-drafting",
    sections: [
      {
        h: "Why surveying firms outsource drafting",
        paras: [
          "Field capacity and drafting capacity rarely peak together. Outsourced survey drafting lets a firm take on more field work without hiring for production peaks — drawings are prepared by an external team while licensed staff keep survey judgment, review, and certification.",
        ],
      },
      {
        h: "Prepare your standards once",
        paras: [
          "The single biggest quality lever is a good standards package: your CAD template, layer structure, plot styles, symbol library, and one or two example sheets you consider well drafted. A drafting partner applies these to every project, so the output looks like your own production.",
          "Include written drafting instructions with each project: scope, required sheets, annotation expectations, and the delivery format.",
        ],
      },
      {
        h: "Structure the work order",
        paras: [
          "Each project should start with a written confirmation: inputs supplied, deliverables expected, assumptions, revision expectations, and delivery date. This protects both sides and turns quality control into a checklist rather than a negotiation.",
        ],
      },
      {
        h: "Keep review where it belongs",
        paras: [
          "Professional review stays in-house by design. The drafting partner flags unclear data in writing and waits for direction; your surveyor reviews the drafted sheets, requests revisions, and certifies. Boundary determination, adjustments, and sign-off never leave your office.",
        ],
      },
      {
        h: "Common pitfalls to avoid",
        list: [
          "Sending data without written instructions — assumptions creep in",
          "Mixing standards between projects — keep one template per deliverable type",
          "Reviewing only the PDF — spot-check the CAD file's layers and structure too",
          "Starting recurring work without agreed volumes and schedules",
        ],
      },
      {
        h: "A simple starting checklist",
        list: [
          "One template and one example sheet per deliverable type",
          "A short drafting-instructions form your PMs can reuse",
          "An agreed secure file-transfer method",
          "A named reviewer on your side for each project",
        ],
        paras: [
          "With those four in place, outsourced drafting becomes a capacity dial you can turn up in busy seasons. Our land survey CAD drafting service is structured around exactly this model.",
        ],
      },
    ],
  },
  {
    slug: "pdf-to-dwg-survey-plan-conversion",
    title: "PDF to DWG: How Survey Plan Conversion Actually Works",
    description:
      "What really happens when a scanned survey plan becomes an editable DWG — redrawing versus autotracing, scaling to stated dimensions, and the quality checks that matter.",
    date: "2026-10-17",
    readMinutes: 6,
    tag: "Conversion",
    relatedService: "pdf-to-cad-conversion",
    sections: [
      {
        h: "Why convert PDF plans to CAD at all",
        paras: [
          "Survey archives are full of plans that exist only as scans or PDFs. When a property is resurveyed, subdivided, or redeveloped, that legacy geometry has to come into CAD to be reused — and a flat image cannot be queried, layered, or edited.",
        ],
      },
      {
        h: "Redrawing beats autotracing",
        paras: [
          "Autotrace software converts pixels to polylines, but the result is thousands of meaningless segments with no layers, no symbols, and no intelligence. Professional PDF to DWG conversion means redrawing: a drafter rebuilds the plan as true CAD geometry, using the dimensions and notes on the source as the authority.",
          "The output is a drawing that behaves like a drawing — snap-able endpoints, real text, organized layers.",
        ],
      },
      {
        h: "Scaling to stated dimensions",
        paras: [
          "Scans are rarely true to scale. Conversion anchors the redraw to the stated bearings and distances on the plan, not to the pixel geometry. Where a source lacks dimensional references, the approach is agreed with the client before drafting — never improvised.",
        ],
      },
      {
        h: "Layers, symbols, and title blocks",
        paras: [
          "A good conversion is indistinguishable from native production: linework on your standard layers, symbols from your library, annotation in your fonts and styles, and the title block rebuilt on your template. This is what makes converted plans safe to reuse in new projects.",
        ],
      },
      {
        h: "Quality checks that matter",
        list: [
          "Overlay check: the redraw plotted over the source to catch shifts and omissions",
          "Dimension check: stated distances verified against drawn geometry",
          "Legibility log: anything unreadable in the source flagged in writing",
          "Structure check: layers, blocks, and text styles match the agreed standard",
        ],
      },
      {
        h: "When conversion makes sense",
        paras: [
          "Conversion pays off when legacy plans feed new work: resurveys, subdivision of old parcels, record modernization, or building a CAD archive from paper. For whole archives, batch conversion with fixed standards is the most economical route. See our PDF to CAD conversion service for how batched work is structured.",
        ],
      },
    ],
  },
  {
    slug: "what-is-alta-nsps-survey-drafting",
    title: "What Is ALTA/NSPS Survey Drafting? A Practical Overview",
    description:
      "What an ALTA/NSPS land title survey involves, what Table A items are, and how drafting support fits into a surveyor-led ALTA production workflow.",
    date: "2026-10-17",
    readMinutes: 5,
    tag: "ALTA/NSPS",
    relatedService: "alta-nsps-survey-drafting",
    sections: [
      {
        h: "What an ALTA/NSPS survey is",
        paras: [
          "The ALTA/NSPS Land Title Survey is a standardized survey product used in US commercial property transactions. It follows the Minimum Standard Detail Requirements agreed by the American Land Title Association and the National Society of Professional Surveyors, giving lenders, title companies, and buyers a consistent picture of the property.",
        ],
      },
      {
        h: "Table A items, in plain terms",
        paras: [
          "Table A is a menu of optional items — utilities, parking counts, flood zone notes, adjoining features, and more — that the client selects for the survey to address. Each instructed item adds drafting work: additional features to show, notes to place, and references to cite.",
        ],
      },
      {
        h: "Where drafting support fits",
        paras: [
          "The surveyor directs; the drafting team produces. In a typical supported workflow, the surveyor supplies the field data, the instructed Table A selections, their template, and their checklist — and the drafting partner prepares the plan sheets, annotates the instructed items, and turns revisions around as directed.",
        ],
      },
      {
        h: "What stays with the licensed surveyor",
        paras: [
          "Everything professional: boundary determination, records research decisions, resolution of conflicts, certification, and the final stamp. Drafting support accelerates production; it never replaces the surveyor's judgment or responsibility.",
        ],
      },
      {
        h: "Typical inputs and deliverables",
        list: [
          "Inputs: field data, title commitment references, Table A selections, template, checklist",
          "Deliverables: plan sheets on your template, instructed items annotated, PDF plots for review",
        ],
        paras: [
          "For firms running ALTA volume, our ALTA/NSPS survey drafting service is built around your checklist and your certification workflow.",
        ],
      },
    ],
  },
];

export const getPost = (slug) => POSTS.find((p) => p.slug === slug);
